import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Megaphone, X } from 'lucide-react';
import authService from '../services/auth.service';
import { useCursorTopic } from '../utils/liveCursors';
import '../styles/Broadcast.css';

/**
 * Annonces en direct (DevTool → tous les navigateurs connectés).
 *
 * Écoute le topic Mercure global `spot/broadcast` et affiche chaque message
 * en toast en haut à gauche, accompagné d'un son de notification synthétisé
 * (Web Audio — aucun fichier). Monté une seule fois dans le layout de l'appli.
 *
 * Limite navigateur : le son ne peut jouer que si l'utilisateur a déjà
 * interagi avec la page (politique d'autoplay). Sinon le toast s'affiche
 * silencieusement.
 */

const AUTO_DISMISS_MS = 15000;

/** Petit « ding » à deux notes, plus grave et insistant pour `important`. */
function playChime(important) {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }
        const notes = important ? [523.25, 392.0, 523.25] : [830.61, 1046.5];
        notes.forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const t0 = ctx.currentTime + i * 0.16;
            gain.gain.setValueAtTime(0, t0);
            gain.gain.linearRampToValueAtTime(0.18, t0 + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.45);
            osc.connect(gain).connect(ctx.destination);
            osc.start(t0);
            osc.stop(t0 + 0.5);
        });
        setTimeout(() => ctx.close().catch(() => {}), 1600);
    } catch {
        // pas de son : le toast suffit
    }
}

const BroadcastToasts = () => {
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    useCursorTopic('spot/broadcast', (msg) => {
        if (!msg?.id || !msg.message) return;
        // L'émetteur voit aussi son annonce : confirmation que la diffusion marche
        setToasts((prev) => (prev.some((t) => t.id === msg.id) ? prev : [...prev, msg]));
        playChime(msg.niveau === 'important');
        setTimeout(() => dismiss(msg.id), AUTO_DISMISS_MS);
    }, Boolean(authService.getCurrentUser()?.id));

    if (toasts.length === 0) return null;

    return createPortal(
        <div className="bc-layer" role="status" aria-live="polite">
            {toasts.map((t) => (
                <div key={t.id} className={`bc-toast${t.niveau === 'important' ? ' bc-toast--important' : ''}`}>
                    <span className="bc-icon">
                        {t.niveau === 'important' ? <AlertTriangle size={17} /> : <Megaphone size={17} />}
                    </span>
                    <div className="bc-body">
                        <div className="bc-head">
                            <span className="bc-from">{t.de || 'Annonce'}</span>
                            <span className="bc-tag">{t.niveau === 'important' ? 'Important' : 'Annonce'}</span>
                        </div>
                        <div className="bc-message">{t.message}</div>
                    </div>
                    <button type="button" className="bc-close" onClick={() => dismiss(t.id)} aria-label="Fermer">
                        <X size={14} />
                    </button>
                </div>
            ))}
        </div>,
        document.body,
    );
};

export default BroadcastToasts;
